import React from 'react';

import { Show } from '@ariksa/compliance-policies';
import { Box, Flex, HStack, Stack } from '@chakra-ui/react';
import { sum } from 'lodash';
import { customTheme } from 'theme';

import {
  renderSeverityCountTags,
  renderStatusTag,
} from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { QuestionIcon } from 'components/Icons';
import { BarProgress } from 'components/Visualization/Progress/BarProgress';

export const statusOptions = [
  { label: 'All', value: Show.All },
  { label: 'Violation', value: Show.Failed },
  { label: 'Passed', value: Show.Passed },
];

export const renderStatus = (upper, lower) => {
  return (
    <Flex flexDir="column" alignItems="flex-start">
      <Box pb="2">
        ({upper}/{lower} - {Math.round((upper / lower) * 100)}
        %)
      </Box>
      <Flex
        w="full"
        h="full"
        alignItems="center"
        justifyContent="space-between"
      >
        <BarProgress total={lower} value={upper} width="full" />
      </Flex>
    </Flex>
  );
};

export const renderCompliantEntities = ({ value }) => {
  const compliant = value?.total_entities - (value?.entities_with_issues ?? 0);
  const total = value?.total_entities;

  return (
    <Stack color="gray.250" spacing={1} align="start">
      <Box>
        {compliant || 0} / {total} ({Math.round((compliant / total) * 100)}
        %)
      </Box>
      <BarProgress total={total} value={compliant} width="full" />
    </Stack>
  );
};

export const renderTableHeaderWithTooltip = (
  label: string,
  tooltip: string,
  justify?: string,
) => {
  return (
    <HStack justify={justify}>
      <Box>{label}</Box>
      <CustomTooltip label={tooltip} w={24}>
        <Box boxSize={4} bg="primary" color="white" borderRadius="50%">
          <QuestionIcon />
        </Box>
      </CustomTooltip>
    </HStack>
  );
};

export const renderComplianceStatus = ({ row }) => {
  const value = row?.check_stats?.failed;
  const total = sum(Object.values(value));
  return renderStatusTag(!!total ? 'FAIL' : 'PASS');
};

export const renderComplianceBarProgress = (
  passed,
  total,
  percent,
  label = 'policy checks passed out of',
  percentFontSize = '2xl',
  barHeight = 3.5,
) => (
  <HStack w="full">
    <Stack spacing={0} w="full">
      <Box fontSize="sm" color={customTheme.colors.gray[300]}>
        {formatNumber(passed)} {label} {formatNumber(total)}
      </Box>
      <Box flex={1} w="full" pb={1}>
        <BarProgress value={passed} total={total} h={barHeight} />
      </Box>
    </Stack>

    <Box w={7} fontSize={percentFontSize} whiteSpace="nowrap" pt={3}>
      {percent}%
    </Box>
  </HStack>
);

export const renderPassedChecks = ({ value }) => {
  const passed = value?.passed;
  const total = value?.total_performed;
  return (
    <Box textAlign="start">
      {renderComplianceBarProgress(
        passed,
        total,
        !total ? 100 : Math.round((passed / total) * 100),
        'out of',
        'lg',
        2.5,
      )}
    </Box>
  );
};

export const renderExceptionsHeader = renderTableHeaderWithTooltip(
  'Exceptions',
  'Entities with exception for this policy',
  'center',
);

export const renderViolations = ({ value }) =>
  renderSeverityCountTags({ value: value?.failed });

export const complianceStatusOptions = [
  { label: 'Passed', value: Show.Passed },
  { label: 'Failed', value: Show.Failed },
];
